
import Button from '../base/Button';
import { useState } from 'react';

export default function Footer() {
  const [showExpertModal, setShowExpertModal] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:info@djthaika.in';
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:18005700229';
  };

  const handleSocialClick = (platform: string) => {
    const urls = {
      linkedin: 'https://linkedin.com/company/djthaika',
      twitter: 'https://twitter.com/djthaika',
      facebook: 'https://facebook.com/djthaika',
      instagram: 'https://instagram.com/djthaika'
    };
    window.open(urls[platform as keyof typeof urls], '_blank');
  };

  const handleNewsletterSubscribe = () => {
    const subject = 'Newsletter Subscription';
    const body = 'Hello DJT HAIKA team,%0D%0A%0D%0AI would like to subscribe to your newsletter to receive updates about EV charging solutions.%0D%0A%0D%0AThank you!';
    window.location.href = `mailto:info@djthaika.in?subject=${subject}&body=${body}`;
  };

  const handleServiceClick = (serviceType: string) => {
    // First scroll to services section
    scrollToSection('services');
    
    // Wait for scroll to complete, then trigger service modal
    setTimeout(() => {
      // Dispatch custom event to trigger specific service modal
      const event = new CustomEvent('openServiceModal', { 
        detail: { serviceType } 
      });
      window.dispatchEvent(event);
    }, 1000);
  };

  const handleServiceInquiry = (serviceName: string) => {
    const subject = `Inquiry about ${serviceName}`;
    const body = `Hello DJT HAIKA team,%0D%0A%0D%0AI am interested in learning more about your ${serviceName}.%0D%0A%0D%0APlease provide detailed information including:%0D%0A- Technical specifications%0D%0A- Pricing details%0D%0A- Installation timeline%0D%0A- Warranty coverage%0D%0A%0D%0AThank you!`;
    window.location.href = `mailto:info@djthaika.in?subject=${subject}&body=${body}`;
  };

  // New handlers for Contact Expert modal
  const handleContactExpert = () => {
    setShowExpertModal(true);
  };

  const handlePhoneCall = () => {
    window.location.href = 'tel:18005700229';
    setShowExpertModal(false);
  };

  const handleEmailExpert = () => {
    window.location.href = 'mailto:info@djthaika.in?subject=Expert Consultation Request&body=Hello DJT HAIKA team,%0D%0A%0D%0AI need expert consultation for EV charging solutions.%0D%0A%0D%0APlease contact me at your earliest convenience.%0D%0A%0D%0AThank you!';
    setShowExpertModal(false);
  };

  const handleScheduleCall = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    setShowExpertModal(false);
  };

  return (
    <>
      <footer className="bg-gray-900 text-white">
        {/* Newsletter Section */}
        <div className="border-b border-gray-800">
          <div className="container mx-auto px-4 py-12">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Stay Updated with EV Innovations
              </h3>
              <p className="text-gray-300 mb-8 text-lg">
                Get the latest news, updates, and insights about electric vehicle charging technology
              </p>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-300 px-8 py-3"
                onClick={handleNewsletterSubscribe}
              >
                <i className="ri-mail-line mr-2"></i>
                Subscribe to Newsletter
              </Button>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-6">
              <div>
                <img 
                  src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/83669deabdd1c2a98c79f1755e4f7c6c.png" 
                  alt="DJT HAIKA" 
                  className="h-12 w-auto mb-4"
                />
                <p className="text-gray-300 leading-relaxed mb-4">
                  Leading provider of innovative EV charging solutions in India. Powering the future of sustainable transportation.
                </p>
              </div>
              
              <div className="flex space-x-4">
                <button 
                  onClick={() => handleSocialClick('linkedin')}
                  className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </button>
                <button 
                  onClick={() => handleSocialClick('twitter')}
                  className="w-10 h-10 bg-gray-800 hover:bg-blue-400 rounded-full flex items-center justify-center transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-twitter-fill text-lg"></i>
                </button>
                <button 
                  onClick={() => handleSocialClick('facebook')}
                  className="w-10 h-10 bg-gray-800 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </button>
                <button 
                  onClick={() => handleSocialClick('instagram')}
                  className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-instagram-fill text-lg"></i>
                </button>
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('home')}
                    className="text-gray-300 hover:text-cyan-400 transition-colors-duration-300 cursor-pointer block"
                  >
                    Look
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('about')}
                    className="text-gray-300 hover:text-cyan-400 transition-colors-duration-300 cursor-pointer block"
                  >
                    Know Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('products')}
                    className="text-gray-300 hover:text-cyan-400 transition-colors-duration-300 cursor-pointer block"
                  >
                    Merchandise
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('services')}
                    className="text-gray-300 hover:text-cyan-400 transition-colors-duration-300 cursor-pointer block"
                  >
                    CMS
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="text-gray-300 hover:text-cyan-400 transition-colors-duration-300 cursor-pointer block"
                  >
                    Mail Drop
                  </button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-bold mb-6">Our Services</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => handleServiceClick('dc-fast-charging')}
                    className="text-gray-300 hover:text-green-400 transition-colors-duration-300 cursor-pointer text-left block w-full"
                  >
                    <i className="ri-flashlight-line text-green-400 mr-2"></i>
                    DC Fast Charging
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleServiceClick('ac-charging')}
                    className="text-gray-300 hover:text-green-400 transition-colors-duration-300 cursor-pointer text-left block w-full"
                  >
                    <i className="ri-home-line text-green-400 mr-2"></i>
                    AC Charging Solutions
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleServiceClick('installation')}
                    className="text-gray-300 hover:text-green-400 transition-colors-duration-300 cursor-pointer text-left block w-full"
                  >
                    <i className="ri-tools-line text-green-400 mr-2"></i>
                    Installation & Setup
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleServiceClick('maintenance')}
                    className="text-gray-300 hover:text-green-400 transition-colors-duration-300 cursor-pointer text-left block w-full"
                  >
                    <i className="ri-settings-line text-green-400 mr-2"></i>
                    Maintenance Support
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleServiceClick('consulting')}
                    className="text-gray-300 hover:text-green-400 transition-colors-duration-300 cursor-pointer text-left block w-full"
                  >
                    <i className="ri-chat-3-line text-green-400 mr-2"></i>
                    Consulting Services
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleServiceClick('smart-management')}
                    className="text-gray-300 hover:text-green-400 transition-colors-duration-300 cursor-pointer text-left block w-full"
                  >
                    <i className="ri-smartphone-line text-green-400 mr-2"></i>
                    Smart Management
                  </button>
                </li>
              </ul>
              
              {/* Service Contact CTA */}
              <div className="mt-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                <h5 className="text-white font-semibold mb-2 text-sm">Need a Custom Solution?</h5>
                <p className="text-gray-400 text-xs mb-3">Get personalized recommendations for your EV charging needs</p>
                <button 
                  type="button" 
                  className="font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg px-4 py-2 text-sm rounded-md w-full bg-green-600 hover:bg-green-700 text-xs py-2"
                  onClick={handleContactExpert}
                >
                  <i className="ri-customer-service-2-line mr-1"></i>
                  Contact Expert
                </button>
              </div>
            </div>
            
            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-bold mb-6">Get In Touch</h4>
              <div className="space-y-4">
                <div 
                  className="flex items-center space-x-3 cursor-pointer hover:text-cyan-400 transition-colors-duration-300"
                  onClick={handlePhoneClick}
                >
                  <i className="ri-phone-line text-xl text-cyan-400"></i>
                  <span className="text-gray-300">1-800-570-0229</span>
                </div>
                
                <div 
                  className="flex items-center space-x-3 cursor-pointer hover:text-cyan-400 transition-colors-duration-300"
                  onClick={handleEmailClick}
                >
                  <i className="ri-mail-line text-xl text-cyan-400"></i>
                  <span className="text-gray-300">info@djthaika.in</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <i className="ri-time-line text-xl text-cyan-400"></i>
                  <div>
                    <p className="text-gray-300">24/7 Support</p>
                    <p className="text-gray-400 text-sm">Always available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Footer */}
        <div className="border-t border-gray-800">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2024 DJT HAIKA. All rights reserved. Powering India's EV revolution.
              </div>
              <div className="flex space-x-6 text-sm">
                <a href="#privacy" className="text-gray-400 hover:text-cyan-400 transition-colors-duration-300">
                  Privacy Policy
                </a>
                <a href="#terms" className="text-gray-400 hover:text-cyan-400 transition-colors-duration-300">
                  Terms of Service
                </a>
                <a href="#cookies" className="text-gray-400 hover:text-cyan-400 transition-colors-duration-300">
                  Cookie Policy
                </a>
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-cyan-400 transition-colors-duration-300"
                >
                  Made with Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Expert Modal */}
      {showExpertModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full mx-4 p-6 md:p-8 relative animate-scale-in max-h-[90vh] overflow-y-auto">
            {/* Enhanced Close Button */}
            <button
              onClick={() => setShowExpertModal(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 shadow-lg z-10 hover:shadow-xl transform hover:scale-105"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Connect with Our EV Expert</h3>
              <p className="text-gray-600 mb-6 text-base md:text-lg">Get instant expert guidance for your EV charging needs. Our specialists are available 24/7 to help you make the right choice.</p>
              
              <div className="space-y-4">
                <button 
                  className="w-full justify-center bg-green-600 hover:bg-green-700 py-3 md:py-4 px-4 rounded-lg text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm md:text-base"
                  onClick={handlePhoneCall}
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                  <span className="ml-2 text-green-200">1-800-570-0229</span>
                </button>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button 
                    className="w-full justify-center bg-purple-600 hover:bg-purple-7 0 py-3 md:py-4 px-4 rounded-lg text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm md:text-base"
                    onClick={handleEmailExpert}
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Email Expert
                    <span className="ml-1 text-purple-200 text-xs">Quick Response</span>
                  </button>
                  
                  <button 
                    className="w-full justify-center bg-cyan-600 hover:bg-cyan-700 py-3 md:py-4 px-4 rounded-lg text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm md:text-base"
                    onClick={() => window.open('https://wa.me/911234567890?text=Hi, I need EV charging consultation', '_blank')}
                  >
                    <i className="ri-chat-3-line mr-2"></i>
                    Live Chat
                    <span className="ml-1 text-cyan-200 text-xs">AI Assistant</span>
                  </button>
                </div>
                
                <button 
                  className="w-full justify-center bg-blue-600 hover:bg-blue-700 py-3 md:py-4 px-4 rounded-lg text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm md:text-base"
                  onClick={handleScheduleCall}
                >
                  <i className="ri-chat-1-line mr-2"></i>
                  WhatsApp
                  <span className="ml-2 text-blue-200 text-xs">Instant Chat</span>
                </button>
                
                <button 
                  className="w-full justify-center bg-orange-600 hover:bg-orange-700 py-3 md:py-4 px-4 rounded-lg text-white font-semibold transition-all duration-300 flex items-center cursor-pointer text-sm md:text-base"
                  onClick={handleScheduleCall}
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Site Visit & Consultation
                </button>
              </div>
              
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-center mb-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                  <p className="text-sm font-bold text-green-800">Expert Available Now</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center text-xs">
                  <div>
                    <p className="font-medium text-green-800">Response Time</p>
                    <p className="text-green-600">Under 2 minutes</p>
                  </div>
                  <div>
                    <p className="font-medium text-green-800">Consultation</p>
                    <p className="text-green-600">100% Free</p>
                  </div>
                  <div>
                    <p className="font-medium text-green-800">24/7 Support</p>
                    <p className="text-green-600">Always Available</p>
                  </div>
                </div>
                <div className="flex justify-center items-center mt-3 space-x-4 text-xs text-green-700">
                  <div className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-1"></i>
                    <span>5+ Years Experience</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-1"></i>
                    <span>1500+ Projects</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-1"></i>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
